import { useAtomValue } from 'jotai';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { NavigationScreenSpaceState } from '@gitbook/app-navigation';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveClient, useHiveComputedRevision } from '@gitbook/hive-react';
import { useSafeContext } from '@gitbook/util-react-hooks';
import { getSuspense } from '@gitbook/util-suspense-cache';

import { hasAnyLibraryChange } from '../Library/util';
import { findActiveDiffPages } from './firstActiveDiffPage';
import { diffOptionsState } from './options';

type DiffMode = 'all-pages' | 'only-changes';
interface DiffContextType {
    /**
     * Mode for the diff. If null, no diff is shown.
     */
    diffMode: DiffMode | null;

    /** The diff result, only if we the diffMode exists. */
    diff: hiveCore.PagesDiffResult | null;

    /** Diff for revision-level tags. Null when not in diff mode. */
    tagsDiff: hiveCore.TagsDiffResult | null;

    /** Diff for revision-level reusable contents. Null when not in diff mode. */
    reusableContentsDiff: hiveCore.ReusableContentsDiffResult | null;

    /** Diff for revision-level files (covers both files and images). Null when not in diff mode. */
    filesDiff: hiveCore.FilesDiffResult | null;

    /** Diff for revision-level (space) variables. Null when not in diff mode. */
    variablesDiff: hiveCore.VariablesDiffResult | null;

    /** Whether any library item (tag, reusable content, file, variable) has changed. */
    hasLibraryChanges: boolean;

    /**
     * Base revision used for the diff. If undefined, no diff is shown.
     */
    baseRevision: hiveCore.Revision | undefined;

    /**
     * The change request the diff is being viewed in, if any.
     */
    changeRequest: api.ChangeRequest | undefined;

    /**
     * The "head" revision being diffed against the base. Exposed so diff
     * renderers can resolve the HEAD (after) side of a comparison regardless of
     * which side's page they were given as a prop (matters for split view).
     */
    currentRevision: hiveCore.Revision;

    /**
     * The pages (in tree order) that have a visible diff in the current revision — the single
     * source of truth for "which pages are changed". Empty when no diff is shown.
     */
    activeDiffPages: hiveCore.RevisionTreePage[];

    /**
     * Ids of pages that have a changed descendant, so the page tree can expand to reveal nested
     * changes. Computed once per diff. Empty when no diff is shown.
     */
    changedAncestorPageIds: Set<string>;
}

/**
 * Holds *what* is being diffed: a single space-wide value (base/current revisions
 * and the computed page/library diffs), provided once near the space root and
 * shared by every consumer — including both split-view columns.
 *
 * Not to be confused with the side-context in `./DiffSideContext` (`useDiffSide`),
 * which is a per-column value telling a subtree *which* split column it renders.
 */
export const DiffContext = React.createContext<DiffContextType | null>(null);

export function useDiffContext(): DiffContextType {
    return useSafeContext(DiffContext, 'DiffContext is not available');
}

/**
 * Return the computed page-tree diff status for a page, when a page diff is active.
 */
export function usePageDiffStatus(pageId: string): hiveCore.PageDiffStatus | undefined {
    const diffContext = React.useContext(DiffContext);
    return diffContext?.diff?.diffs.get(pageId)?.type as hiveCore.PageDiffStatus | undefined;
}

/**
 * Provide high-level diff context for the entire space. It does the following:
 *
 * - Determine whether we need to show a diff (and the complexity of the diff) based on the current screen
 * - Determine which revision to use as the base for the diff
 * - Compute the diff for the pages
 *
 * It does not compute diffs for content, which is done at a lower level.
 */
export function DiffContextProvider(props: {
    children: React.ReactNode;
    revision: hiveCore.Revision;
    sourceRevision: hiveCore.Revision;
    contentId: string;
    changeRequest: api.ChangeRequest | undefined;
    screen: NavigationScreenSpaceState | undefined;
}): React.ReactElement {
    const { revision, sourceRevision, contentId, screen, changeRequest, children } = props;

    const hiveClient = useHiveClient();
    const diffOptions = useAtomValue(diffOptionsState);

    const baseRevisionId = getBaseRevisionId(revision, changeRequest, screen, sourceRevision);
    const baseRevision =
        useHiveComputedRevision(
            baseRevisionId
                ? getSuspense(
                      hiveClient.store.getRevision(contentId, baseRevisionId, { metadata: false })
                  )
                : undefined,
            {
                suspendUntilComplete: true,
            }
        )?.revision ?? null;

    const diffMode = ((): DiffMode | null => {
        if (!baseRevision || !screen) {
            // without a base revision, no diff
            return null;
        }

        if (screen.revision && screen.mode !== 'diff') {
            // when viewing the history but with diff disabled
            return null;
        }

        if (screen.mode === 'diff') {
            // if we're in explicit diff mode, full diff
            return diffOptions.onlyShowChangedPages ? 'only-changes' : 'all-pages';
        }

        return null;
    })();

    const value = React.useMemo<DiffContextType>(() => {
        // If there's no baseRevision or we are not supposed to show a diff
        if (diffMode === null || !baseRevision) {
            return {
                diff: null,
                tagsDiff: null,
                reusableContentsDiff: null,
                filesDiff: null,
                variablesDiff: null,
                hasLibraryChanges: false,
                baseRevision: baseRevision ?? undefined,
                changeRequest,
                currentRevision: revision,
                activeDiffPages: [],
                changedAncestorPageIds: new Set<string>(),
                diffMode,
                diffOptions: {
                    onlyShowChangedPages: diffOptions.onlyShowChangedPages,
                },
            };
        }

        const diff = hiveCore.diffRevisionPages(revision, baseRevision, {
            includeDeleted: true,
            includeUnchanged: diffMode === 'all-pages',

            // Ignore pages with changes in metadata / variables. We don't have diff UI to display those.
            compareMetadata: false,
        });

        // Library diffs always include all items (changed + unchanged) so that the Library views can
        // render the full list and use the diffs map for per-item lookups. The "Show changes only"
        // filter is applied in the views themselves.
        const tagsDiff = hiveCore.diffRevisionTags(revision, baseRevision, {
            includeDeleted: true,
        });
        const reusableContentsDiff = hiveCore.diffRevisionReusableContents(revision, baseRevision, {
            includeDeleted: true,
        });
        const filesDiff = hiveCore.diffRevisionFiles(revision, baseRevision, {
            includeDeleted: true,
        });
        const variablesDiff = hiveCore.diffRevisionVariables(
            revision.variables,
            baseRevision.variables
        );

        const hasLibraryChanges = hasAnyLibraryChange({
            variablesDiff,
            tagsDiff,
            reusableContentsDiff,
            filesDiff,
        });

        return {
            diff,
            tagsDiff,
            reusableContentsDiff,
            filesDiff,
            variablesDiff,
            hasLibraryChanges,
            baseRevision,
            changeRequest,
            currentRevision: revision,
            activeDiffPages: findActiveDiffPages({
                pages: diff.pages,
                currentRevision: revision,
                baseRevision,
            }),
            changedAncestorPageIds: hiveCore.getPagesWithChangedDescendant(diff.pages),
            diffMode,
            diffOptions: {
                onlyShowChangedPages: diffOptions.onlyShowChangedPages,
            },
        };
    }, [revision, baseRevision, changeRequest, diffMode, diffOptions.onlyShowChangedPages]);

    return <DiffContext.Provider value={value}>{children}</DiffContext.Provider>;
}

/**
 * Determine the base revision for the diff, and whether the diff should be deep.
 */
function getBaseRevisionId(
    revision: hiveCore.Revision | null | undefined,
    changeRequest: api.ChangeRequest | null | undefined,
    screen: NavigationScreenSpaceState | undefined,
    sourceRevision?: hiveCore.Revision | null
): string | undefined {
    if (screen?.baseRevision) {
        return screen.baseRevision;
    }

    if (changeRequest) {
        return changeRequest.revisionMergedAncestor || changeRequest.revisionInitial;
    }

    if (screen?.revision) {
        const historicalRevision = sourceRevision ?? revision;
        return historicalRevision
            ? (hiveCore.getRevisionLinearParent(historicalRevision) ?? undefined)
            : undefined;
    }

    return undefined;
}
